import {
  ApplicationConfig, inject, Injector, provideAppInitializer, provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core'
import { provideRouter, withRouterConfig } from '@angular/router'
import { provideHttpClient, withInterceptors } from '@angular/common/http'
import { Amplify } from 'aws-amplify'
import { routes } from './app.routes'
import { authInterceptor } from './core/interceptors/auth.interceptor'
import { activityInterceptor } from './core/interceptors/activity.interceptor'
import { ActivityTrackerService } from './core/activity/activity-tracker.service'
// environment.ts is replaced by environment.development.ts in dev builds
// (see angular.json fileReplacements). Both export an identical `environment` const.
import { environment } from '../environments/environment'

// ── Amplify configuration ─────────────────────────────────────────────────────
// Configure Amplify once at app startup, before any component or service uses it.
//
// What Amplify does for us here:
//   - Manages the OAuth Authorization Code + PKCE flow with the Cognito Hosted UI.
//   - Exchanges the auth code for tokens (ID token, access token, refresh token).
//   - Stores tokens in localStorage so sessions survive page reloads.
//   - Automatically refreshes the access token using the refresh token when needed.
//   - Provides signInWithRedirect() / signOut() / fetchAuthSession() APIs used
//     by AuthService.
//
// The environment values are injected by angular.json fileReplacements:
//   - dev builds use environment.development.ts (generated by infrastructure/setup-dev.sh)
//   - prod builds use environment.ts (populated before deployment)
// Derive redirect URLs from the current origin so the same build works from
// both the local dev server (http://localhost:4200) and the CloudFront URL.
// Cognito has both origins registered in its allowed callbackUrls/logoutUrls.
const { origin } = window.location

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: environment.cognito.userPoolId,
      userPoolClientId: environment.cognito.clientId,
      loginWith: {
        oauth: {
          // The Cognito Hosted UI domain (hostname only, no protocol).
          domain: environment.cognito.domain,
          // Scopes requested from Cognito:
          //   openid — required for ID token issuance
          //   email  — populates the `email` claim in the ID token
          //   profile — populates the `name` claim in the ID token
          scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
          // Where Cognito redirects after successful login.
          // The Angular app handles this route in AuthCallbackComponent.
          redirectSignIn: [`${origin}/auth/callback`],
          // Where Cognito redirects after sign-out.
          redirectSignOut: [`${origin}/login`],
          // 'code' enables the Authorization Code grant.
          // Amplify automatically applies PKCE to this flow for security.
          responseType: 'code',
        },
      },
    },
  },
})

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    // 'computed': a navigation the leave guard cancels (Back, then Keep editing) restores the
    // browser history entry instead of replacing it, so Back keeps working afterwards.
    provideRouter(routes, withRouterConfig({ canceledNavigationResolution: 'computed' })),
    provideHttpClient(withInterceptors([authInterceptor, activityInterceptor])),
    // Editor Activity Tracking: inert until this runs; active only on the Editor routes.
    provideAppInitializer(() => inject(ActivityTrackerService).init(inject(Injector))),
  ],
}
